import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ApiEndpoints,
  baseQueryWithAuth,
  LocalStorageKeys,
} from "../../../core";
import { getToken } from "../../../utils/auth";
import { toast } from "../../../core/toast";

type UploadMediaResponse = any;

export interface UploadMediaRequest {
  file: File | Blob;
  size?: number;
  fileName?: string;
  fields?: Record<string, string | number | boolean>;
  onUploadProgress?: (percent: number) => void;
}

export const fileApi = createApi({
  reducerPath: "fileApi",
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    uploadThumbnail: build.mutation<UploadMediaResponse, UploadMediaRequest>({
      async queryFn(arg) {
        const { file, size, fileName, fields, onUploadProgress } =
          arg as UploadMediaRequest;
        const form = new FormData();

        if (file) {
          const name =
            fileName ||
            (typeof (file as any)?.name === "string"
              ? (file as any).name
              : "thumbnail.jpg");
          form.append("image", file as any, name);
        }

        const resolvedSize =
          typeof size === "number" ? size : (file as File | Blob)?.size ?? 0;
        if (resolvedSize > 0) form.append("size", String(resolvedSize));

        if (fields) {
          for (const [k, v] of Object.entries(fields)) {
            if (v !== undefined && v !== null) form.append(k, String(v));
          }
        }

        return await new Promise<any>((resolve) => {
          try {
            const xhr = new XMLHttpRequest();
            const base = (ApiEndpoints as any).serverURL || "";
            const url = `${base}${ApiEndpoints.uploadThumbnail}`;

            xhr.open("POST", url);

            const token = getToken();

            if (token) {
              xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            }

            if (xhr.upload && typeof onUploadProgress === "function") {
              xhr.upload.onprogress = (e: ProgressEvent) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  try {
                    onUploadProgress(percent);
                  } catch {
                    /* ignore callback errors */
                  }
                }
              };
            }

            xhr.onreadystatechange = () => {
              if (xhr.readyState !== 4) return;
              const ct = xhr.getResponseHeader("content-type") || "";
              let parsed: any = null;
              try {
                parsed = ct.includes("application/json")
                  ? JSON.parse(xhr.responseText)
                  : xhr.responseText;
              } catch {
                parsed = xhr.responseText;
              }
              if (xhr.status >= 200 && xhr.status < 300) {
                const msg =
                  (parsed && (parsed.message || parsed.result?.message)) ||
                  "Thumbnail uploaded";
                toast.success(String(msg));
                resolve({ data: parsed });
              } else {
                const errMsg =
                  (parsed && (parsed.message || parsed.error?.message)) ||
                  "Thumbnail upload failed";
                toast.error(String(errMsg));
                resolve({ error: { status: xhr.status, data: parsed } });
              }
            };

            xhr.onerror = () => {
              toast.error("Thumbnail upload network error");
              resolve({ error: { status: "NETWORK_ERROR", data: null } });
            };

            xhr.send(form);
          } catch (err: any) {
            toast.error("Thumbnail upload XHR error");
            resolve({
              error: { status: "XHR_ERROR", data: err?.message ?? err },
            });
          }
        });
      },
    }),

    uploadVideo: build.mutation<UploadMediaResponse, UploadMediaRequest>({
      async queryFn(arg) {
        const { file, size, fileName, fields, onUploadProgress } =
          arg as UploadMediaRequest;
        const form = new FormData();

        if (file) {
          const name =
            fileName ||
            (typeof (file as any)?.name === "string"
              ? (file as any).name
              : "video.mp4");
          form.append("video", file as any, name);
        }

        const resolvedSize =
          typeof size === "number" ? size : (file as File | Blob)?.size ?? 0;
        if (resolvedSize > 0) form.append("size", String(resolvedSize));

        if (fields) {
          for (const [k, v] of Object.entries(fields)) {
            if (v !== undefined && v !== null) form.append(k, String(v));
          }
        }

        return await new Promise<any>((resolve) => {
          try {
            const xhr = new XMLHttpRequest();
            const base = (ApiEndpoints as any).serverURL || "";
            const url = `${base}${ApiEndpoints.uploadFile}`;

            xhr.open("POST", url);

            const token = getToken();

            if (token) {
              xhr.setRequestHeader("Authorization", `Bearer ${token}`);
            }

            if (xhr.upload && typeof onUploadProgress === "function") {
              xhr.upload.onprogress = (e: ProgressEvent) => {
                if (e.lengthComputable) {
                  const percent = Math.round((e.loaded / e.total) * 100);
                  try {
                    onUploadProgress(percent);
                  } catch {
                    /* ignore callback errors */
                  }
                }
              };
            }

            xhr.onreadystatechange = () => {
              if (xhr.readyState !== 4) return;
              const ct = xhr.getResponseHeader("content-type") || "";
              let parsed: any = null;
              try {
                parsed = ct.includes("application/json")
                  ? JSON.parse(xhr.responseText)
                  : xhr.responseText;
              } catch {
                parsed = xhr.responseText;
              }
              if (xhr.status >= 200 && xhr.status < 300) {
                const msg =
                  (parsed && (parsed.message || parsed.result?.message)) ||
                  "Video uploaded";
                toast.success(String(msg));
                resolve({ data: parsed });
              } else {
                const errMsg =
                  (parsed && (parsed.message || parsed.error?.message)) ||
                  "Video upload failed";
                toast.error(String(errMsg));
                resolve({ error: { status: xhr.status, data: parsed } });
              }
            };

            xhr.onerror = () => {
              toast.error("Video upload network error");
              resolve({ error: { status: "NETWORK_ERROR", data: null } });
            };

            xhr.send(form);
          } catch (err: any) {
            toast.error("Video upload XHR error");
            resolve({
              error: { status: "XHR_ERROR", data: err?.message ?? err },
            });
          }
        });
      },
    }),
  }),
});

export const { useUploadThumbnailMutation, useUploadVideoMutation } = fileApi;
